/** Matches `launcher.js` / `adAnimation.ts`: tablet & phone layout (≤769px). */
export function isLauncherMobileViewport() {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.matchMedia('(max-width: 769px)').matches;
}
/** Max inset (top or bottom) so an assumed-height modal stays on-screen. */
function maxMobileVerticalInset(windowHeight, estimatedModalHeight) {
    return Math.max(0, windowHeight - estimatedModalHeight);
}
/**
 * Clamp positive offset: px from top (0 … max inset).
 * Negative values are handled separately via {@link clampMobileInsetFromBottom}.
 */
export function clampLauncherMobileVerticalOffset(offset, windowHeight, estimatedModalHeight = 560) {
    if (offset <= 0) {
        return 0;
    }
    const maxInset = maxMobileVerticalInset(windowHeight, estimatedModalHeight);
    return Math.min(offset, maxInset);
}
/** `|negative|` = px from bottom; clamped to the same max inset as top. */
export function clampMobileInsetFromBottom(offsetNegative, windowHeight, estimatedModalHeight = 560) {
    const magnitude = Math.abs(offsetNegative);
    const maxInset = maxMobileVerticalInset(windowHeight, estimatedModalHeight);
    return Math.min(magnitude, maxInset);
}
// Utility function to check if we're in a WebView
export function isInWebView() {
    const userAgent = navigator.userAgent;
    const androidWebView = /(?!Chrome\/[0-9]{2}\.0\.)(?!Version\/[0-9]{2}\.)(?!Chrome\/[0-9]{2}\.0\.0\.)(?!CriOS\/[0-9]{2}\.)(?!Version\/[0-9]{2}\.0\.)(?!Trident\/[0-9]{2}\.)(?!MSIE [0-9]{2}\.)(?!Edge\/[0-9]{2}\.)\b(?:(?!like Gecko)\b)?\b(?:(?!Safari\/)\b)?Android.*Version\/[0-9.]+(?:[A-Z]+)?/;
    const iOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/;
    const electronApp = /Electron/;
    return (androidWebView.test(userAgent) ||
        iOSWebView.test(userAgent) ||
        electronApp.test(userAgent));
}
// Calculate positioning styles based on settings
export function getPositionStyles(settings, isMobile) {
    const { ad_position: position = 'center', screen_margin: margin = 20, darken_bg: background = false, darken_bg_non_centered: backgroundNonCentered = false, enable_vertical_offset: verticalOffset = false, mobile_vertical_offset: mobileVerticalOffset = null, } = settings;
    const shouldDarkenBg = (backgroundNonCentered && position !== 'center') ||
        (background && position === 'center');
    const isDesktop = !isMobile && window.matchMedia('(min-width:769px)').matches;
    // Base overlay styles
    // Note: Inside Shadow DOM, position: fixed is relative to shadow root, not viewport
    // Since shadow container is already position: fixed covering viewport, use absolute
    const overlayStyles = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: shouldDarkenBg ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
        zIndex: 10000,
        display: shouldDarkenBg ? 'flex' : 'none',
        padding: isMobile ? '10px' : '20px',
    };
    // Base modal styles
    // Note: Inside Shadow DOM, use absolute instead of fixed since shadow container handles viewport positioning
    const modalStyles = {
        position: 'absolute',
        display: 'grid',
        flexDirection: 'column',
        overflow: 'hidden',
    };
    const rawMobileOffset = mobileVerticalOffset;
    if (isMobile && rawMobileOffset != null) {
        if (rawMobileOffset === 0) {
            overlayStyles.alignItems = 'center';
            overlayStyles.justifyContent = 'center';
            modalStyles.left = '50%';
            modalStyles.top = '50%';
            modalStyles.transform = 'translate(-50%, -50%)';
            modalStyles.marginTop = '0';
            modalStyles.marginBottom = '0';
        }
        else {
            const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
            modalStyles.left = '50%';
            modalStyles.marginTop = '0';
            modalStyles.marginBottom = '0';
            modalStyles.transform = 'translate(-50%, 0)';
            if (rawMobileOffset < 0) {
                const insetBottom = clampMobileInsetFromBottom(rawMobileOffset, windowHeight);
                overlayStyles.alignItems = 'flex-end';
                overlayStyles.justifyContent = 'center';
                modalStyles.top = 'auto';
                modalStyles.bottom = `${insetBottom}px`;
            }
            else {
                const insetTop = clampLauncherMobileVerticalOffset(rawMobileOffset, windowHeight);
                overlayStyles.alignItems = 'flex-start';
                overlayStyles.justifyContent = 'center';
                modalStyles.top = `${insetTop}px`;
                modalStyles.bottom = 'auto';
            }
        }
        return { overlayStyles, modalStyles, usesMobileVerticalOffset: true };
    }
    // Apply positioning logic
    if (isDesktop) {
        // Desktop positioning
        switch (position) {
            case 'center':
            default:
                overlayStyles.alignItems = 'center';
                overlayStyles.justifyContent = 'center';
                if (verticalOffset && position !== 'center') {
                    const windowHeight = window.innerHeight;
                    const modalHeight = 560;
                    const maxMargin = windowHeight - modalHeight;
                    const clampedMargin = Math.max(0, Math.min(margin, maxMargin));
                    modalStyles.marginTop = `${clampedMargin}px`;
                    modalStyles.transform = 'translateX(-50%)';
                }
                break;
            case 'top-center':
                overlayStyles.alignItems = 'flex-start';
                overlayStyles.justifyContent = 'center';
                modalStyles.marginTop = `${margin}px`;
                break;
            case 'bottom-center':
                overlayStyles.alignItems = 'flex-end';
                overlayStyles.justifyContent = 'center';
                modalStyles.marginBottom = `${margin}px`;
                break;
            case 'left-center':
                overlayStyles.alignItems = 'center';
                overlayStyles.justifyContent = 'flex-start';
                modalStyles.marginLeft = `${margin}px`;
                break;
            case 'right-center':
                overlayStyles.alignItems = 'center';
                overlayStyles.justifyContent = 'flex-end';
                modalStyles.marginRight = `${margin}px`;
                break;
            case 'top-left':
                overlayStyles.alignItems = 'flex-start';
                overlayStyles.justifyContent = 'flex-start';
                modalStyles.marginLeft = `${margin}px`;
                modalStyles.marginTop = `${margin}px`;
                break;
            case 'top-right':
                overlayStyles.alignItems = 'flex-start';
                overlayStyles.justifyContent = 'flex-end';
                modalStyles.marginRight = `${margin}px`;
                modalStyles.marginTop = `${margin}px`;
                break;
            case 'bottom-left':
                overlayStyles.alignItems = 'flex-end';
                overlayStyles.justifyContent = 'flex-start';
                modalStyles.marginLeft = `${margin}px`;
                modalStyles.marginBottom = `${margin}px`;
                break;
            case 'bottom-right':
                overlayStyles.alignItems = 'flex-end';
                overlayStyles.justifyContent = 'flex-end';
                modalStyles.marginRight = `${margin}px`;
                modalStyles.marginBottom = `${margin}px`;
                break;
        }
    }
    else {
        // Mobile defaults to center
        overlayStyles.alignItems = 'center';
        overlayStyles.justifyContent = 'center';
    }
    return {
        overlayStyles,
        modalStyles,
        usesMobileVerticalOffset: false,
    };
}
/** Center modals that snap to integer pixels after first layout (see OfferModal). */
export function shouldApplyStickyCenterPosition(settings, options) {
    if (options.embedded || isInWebView()) {
        return false;
    }
    if (options.offerChrome === 'template') {
        return false;
    }
    const isMobile = options.viewportWidth <= 690;
    if (isMobile) {
        // On mobile, mobile_vertical_offset overrides ad_position entirely.
        // null and 0 both resolve to a centered modal; any non-zero value positions
        // the modal at a specific top/bottom pixel and must not be snapped to center.
        const offset = settings.mobile_vertical_offset ?? null;
        return offset === null || offset === 0;
    }
    return (settings.ad_position ?? 'center') === 'center';
}
/**
 * Locks a center modal to integer viewport pixels.
 * Uses offsetHeight (transform-independent) so the result is correct whether the
 * element is mid-animation or at rest. Avoids getBoundingClientRect() which can
 * return the animation from-keyframe position when called before the first paint.
 */
export function calculateStickyPosition(modalElement) {
    if (!modalElement) {
        return null;
    }
    const viewportHeight = window.innerHeight;
    const modalHeight = modalElement.offsetHeight;
    const top = Math.round((viewportHeight - modalHeight) / 2);
    return {
        top: `${top}px`,
        transform: 'translate(-50%, 0%)',
        marginTop: '0',
        marginBottom: '0',
    };
}
